import React from 'react';
import { 
  GraduationCap, 
  BookOpen, 
  Award, 
  Calendar, 
  Laptop, 
  Users 
} from 'lucide-react';

const InstructorPage = () => {
  const services = [
    { icon: GraduationCap, label: "Certified Teaching" },
    { icon: BookOpen, label: "Course Creation" },
    { icon: Award, label: "Professional Certification" },
    { icon: Calendar, label: "Flexible Scheduling" },
    { icon: Laptop, label: "Tech Workshops" },
    { icon: Users, label: "Individual Sessions" }
  ];

  const StarRating = ({ rating, label }) => (
    <div className="card h-100 text-center">
      <div className="card-body d-flex flex-column justify-content-center">
        <div className="d-flex justify-content-center align-items-center mb-2">
          {[...Array(5)].map((_, i) => (
            <span
              key={i}
              className="fs-4"
              style={{ 
                color: i < Math.floor(rating) ? '#ffc107' : i < rating ? '#ffc107' : '#dee2e6' 
              }}
            >
              ★
            </span>
          ))}
          <span className="ms-2 fs-4 fw-bold text-dark">{rating}</span>
        </div>
        <div className="fw-bold text-secondary">{label}</div>
      </div>
    </div>
  );

  return (
    <div 
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #4338ca 100%)'
      }}
    >
      {/* Bootstrap CSS */}
      <link 
        href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css" 
        rel="stylesheet" 
      />
      
      {/* Custom Styles */}
      <style>{`
        .service-card {
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }
        .service-card:hover {
          border-color: #ebebebff;
          transform: translateY(-3px);
        }
        .profile-img {
          width: 250px;
          height: 250px;
          object-fit: center;
          object-position: center;
        }
        .skill-badge {
          background: linear-gradient(135deg, #3b82f6, #00dc04ff);
          color: white;
          transition: all 0.3s ease;
        }
        .skill-badge:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        .fallback-avatar {
          width: 120px;
          height: 120px;
          background: linear-gradient(135deg, #60a5fa, #a78bfa);
          display: none;
        }
      `}</style>

      {/* Main Container */}
      <div className="container py-5">
        <div className="row align-items-start">
          
          {/* Left Content */}
          <div className="col-lg-7 mb-4">
            <h1 className="display-4 fw-bold text-white mb-4">
              Expert Instructor for Your Learning Journey
            </h1>
            <h2 className="fs-4 text-light mb-5">
              Professional Teaching & Mentorship Solutions Under One Roof
            </h2>
            
            {/* Services Grid */}
            <div className="row g-3 mb-5">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="col-6 col-lg-4">
                    <div className="card service-card h-100 text-center p-3">
                      <div className="card-body d-flex flex-column align-items-center">
                        <IconComponent size={32} className="text-primary mb-2" />
                        <span className="small fw-semibold">{service.label}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Content - Instructor Profile */}
          <div className="col-lg-5">
            <div className="card shadow-lg">
              <div className="card-body p-4">
                {/* Profile Image */}
                <div className="text-center mb-4">
                  <div className="d-inline-block position-relative">
                    <img 
                      src="https://cdn.jsdelivr.net/gh/OlewinAcademy/Academy_brochures@main/Mahesh_photo.png" 
                      alt="Bandaru Mahesh - Professional Instructor" 
                      className=" img-fluid rounded-circle shadow profile-img rounded-5"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  </div>
                  
                  {/* Profile Information */}
                  <h3 className="h5 fw-bold text-dark mt-3 mb-1">Bandaru Mahesh</h3>
                  <p className="text-primary fw-semibold small mb-1">Core Instructor/Professor, International Events & Workshop Sponsor</p>
                  <p className="text-gray fw-semibold small mb-1">Founder & CEO of Olewin Group</p><hr/>
                </div>

                {/* Credentials */}
                <div className="mb-1">
                  <div className="small text-secondary mb-1">
                    <strong>Alumnus:</strong> IIT Kharagpur (Integrated PhD)- 2019
                  </div>
                  <div className="small text-secondary mb-1">
                    <strong>Specialization:</strong> Quantum Computing MIT, USA
                  </div>
                  <div className="small text-secondary">
                    <strong>Expertise:</strong> Space Tech (Satellite) Modules,AI, Quantum, Super Intelligence AGI Specialist, Geo-Technical & Energy Sector Engineer  
                  </div>
                </div>

                {/* Description */}
                <div className="small text-muted lh-sm">
                 <p className="mb-0">
  Bandaru Mahesh is a visionary entrepreneur and educator with a proven track record in developing advanced AI-powered solutions 
  for geosciences, energy exploration, space technologies, and sustainability. His pioneering work integrates quantum computing, 
  satellite systems, artificial general intelligence (AGI), large language models (LLMs), machine learning, IoT, embedded systems, 
  civil engineering innovations, and next-generation software systems to solve some of the world's most complex challenges.
</p>

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Expertise Section */}
        <div className="row mt-5">
          <div className="col-12">
            <div className="card shadow-lg">
              <div className="card-body p-4">
                <h4 className="h4 fw-bold text-dark mb-4">Technical Expertise</h4>
                <div className="row g-3">
                  {[
                    "Quantum Computing Specialist",
                    "Space Tech & Satellite Modules",
                    "BlockChain Technology",
                    "AI-Agents & LLM Developer", 
                    "Full Stack Developer (Rust, MERN, Svelte, Web 3.0 & D-Apps)",
                    "Mobile App Developer (Android & iOS)",
                    "Cloud Architect- Aws, Google, Microsoft",
                    "System Designer & Architecture (LLD & HLD)",
                    "GeoScientist & GeoPhysicist,GeoTechnical",
                    " IoT & Embedded Engineer",
                    "MLOps & Big Data for RAG, AGI & LLM Model Developer"
                  ].map((skill, index) => (
                    <div key={index} className="col-12 col-sm-6 col-lg-4">
                      <div className="skill-badge rounded px-3 py-2 text-center small fw-medium shadow-sm">
                        {skill}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ratings Section */}
        <div className="row mt-5 justify-content-center">
          <div className="col-md-6 col-lg-4 mb-3">
            <StarRating rating={4.8} label="Google Reviews" />
          </div>
          <div className="col-md-6 col-lg-4">
            <StarRating rating={4.7} label="Student Reviews" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorPage;